import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://email-warmup.shorthills.ai';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 second timeout to prevent UI freezes
  headers: {
    'Content-Type': 'application/json',
  },
});

// SMART API Call throttling to prevent overload (reduced from 3s to 1s) 
let lastCallTime = {};
const CALL_THROTTLE_MS = 1000; // 1 second minimum between same endpoint calls

// Clear throttling history on page refresh for better initial loads
window.addEventListener('beforeunload', () => {
  lastCallTime = {};
});

// Request interceptor for debugging and throttling
api.interceptors.request.use((config) => {
  console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, config.data || '');
  
  // THROTTLING TEMPORARILY DISABLED - Dashboard needs to load
  // TODO: Implement smarter throttling later
  
  // const endpoint = `${config.method}:${config.url}`;
  // const now = Date.now();
  // 
  // if (lastCallTime[endpoint] && (now - lastCallTime[endpoint] < CALL_THROTTLE_MS)) {
  //   console.warn(`🚫 THROTTLED: ${endpoint} - Last call was ${now - lastCallTime[endpoint]}ms ago`);
  //   return Promise.reject(new Error('Request throttled - too frequent'));
  // }
  // 
  // lastCallTime[endpoint] = now;
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
    return response;
  },
  (error) => {
    console.error(`❌ ${error.config?.method?.toUpperCase()} ${error.config?.url}`, error.response?.data || error.message);
    
    // Better error messages for throttled requests
    if (error.message?.includes('Request throttled')) {
      error.message = 'Loading... (request rate limited)';
      error.isThrottled = true;
    }
    
    return Promise.reject(error);
  }
);

// API service methods
export const apiService = {
  // System status
  getSystemStatus: () => api.get('/api/v1/system/health'),
  
  // Dashboard stats
  getDashboardStats: () => api.get('/api/v1/monitoring/dashboard'),
  
  // Domains
  getDomains: () => api.get('/api/v1/marketing/domains'),
  
  // Email templates
  getEmailTemplates: () => api.get('/api/v1/marketing/templates'),
  createEmailTemplate: (data) => api.post('/api/v1/marketing/templates', data),
  
  // Email logs
  getEmailLogs: (params = {}) => api.get('/api/v1/monitoring/activity', { params }),
  
  // Recipients
  getRecipients: (params = {}) => api.get('/api/v1/marketing/recipients', { params }),
  createRecipient: (data) => api.post('/api/v1/marketing/recipients', data),
  updateRecipient: (id, data) => api.put(`/api/v1/marketing/recipients/${id}`, data),
  deleteRecipient: (id) => api.delete(`/api/v1/marketing/recipients/${id}`),
  
  // Testing endpoints
  sendTestEmail: (data) => api.post('/api/v1/marketing/test-email', data),
  testReplies: (data) => api.post('/api/v1/inbox/test-replies', data),
  runManualWarmup: (data) => api.post('/api/v1/system/trigger-manual-warmup', data),
  testJunkFolders: (account) => api.get(`/api/v1/monitoring/test-junk?account=${account}`),
  
  // Schedule status
  getScheduleStatus: () => api.get('/api/v1/monitoring/schedules'),
  
  // System logs
  getLogs: () => api.get('/api/v1/monitoring/activity'),
  
  // Trigger warmup cycle
  triggerCycle: () => api.post('/api/v1/direct-email/run-cycle'),
  
  // Settings
  getSettings: async () => {
    try {
      console.log('📡 API: Getting settings...');
      const response = await api.get('/api/v1/system/configuration');
      console.log('📡 API: Settings response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Settings GET error:', error);
      throw error;
    }
  },
  updateSettings: async (data) => {
    try {
      console.log('📡 API: Updating settings:', data);
      const response = await api.post('/api/v1/system/configuration', data);
      console.log('📡 API: Update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Settings POST error:', error);
      throw error;
    }
  },

  // Persistent Scheduler
  getPersistentSchedulerStatus: async () => {
    try {
      console.log('📡 API: Getting persistent scheduler status...');
      const response = await api.get('/api/v1/monitoring/schedules');
      console.log('📡 API: Scheduler status response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Scheduler status error:', error);
      throw error;
    }
  },
  clearPersistentScheduler: async () => {
    try {
      console.log('📡 API: Clearing persistent scheduler...');
      const response = await api.delete('/api/v1/monitoring/schedules/clear');
      console.log('📡 API: Clear scheduler response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Clear scheduler error:', error);
      throw error;
    }
  },

  // Email Account Tracking
  getEmailAccounts: async () => {
    try {
      console.log('📡 API: Getting email accounts...');
      const response = await api.get('/api/v1/monitoring/users');
      console.log('📡 API: Email accounts response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Email accounts error:', error);
      throw error;
    }
  },

  getAccountMetrics: async (accountEmail) => {
    try {
      console.log('📡 API: Getting account metrics for:', accountEmail);
      const response = await api.get(`/api/v1/monitoring/users/${encodeURIComponent(accountEmail)}`);
      console.log('📡 API: Account metrics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Account metrics error:', error);
      // Return mock data if the endpoint fails (for mock accounts)
      if (error.response?.status === 404) {
        console.log('📡 API: Using mock metrics for:', accountEmail);
        return {
          success: true,
          data: {
            total_sent: 0,
            delivered: 0,
            opened: 0,
            spam: 0,
            replied: 0,
            last_activity: null,
            delivery_rate: 0,
            open_rate: 0,
            spam_rate: 0,
            reply_rate: 0
          }
        };
      }
      throw error;
    }
  },

  // Agent System Endpoints
  getAgentStatus: async () => {
    try {
      // Placeholder for agent status - not implemented yet
      return { success: true, agents: [], status: 'not_implemented' };
    } catch (error) {
      console.error('📡 API: Agent status error:', error);
      throw error;
    }
  },

  startAgents: async () => {
    try {
      // Placeholder for start agents - not implemented yet
      return { success: true, message: 'Agent system not implemented' };
    } catch (error) {
      console.error('📡 API: Start agents error:', error);
      throw error;
    }
  },

  stopAgents: async () => {
    try {
      // Placeholder for stop agents - not implemented yet
      return { success: true, message: 'Agent system not implemented' };
    } catch (error) {
      console.error('📡 API: Stop agents error:', error);
      throw error;
    }
  },

  triggerAgentCycle: async (cycleType) => {
    try {
      const response = await api.post(`/api/v1/agents/trigger-cycle`, {
        cycle_type: cycleType,
        priority: "high",
        reason: "Manual trigger from UI"
      });
      return response.data;
    } catch (error) {
      console.error('📡 API: Trigger cycle error:', error);
      throw error;
    }
  },

  forceActivateAgent: async (agentType, reason = 'Manual activation') => {
    try {
      const response = await api.post(`/api/v1/agents/force-activate/${agentType}`, { reason });
      return response.data;
    } catch (error) {
      console.error('📡 API: Force activate error:', error);
      throw error;
    }
  },

  setManualOverride: async (agentType, action, reason = 'Manual override') => {
    try {
      const response = await api.post(`/api/v1/agents/manual-override/${agentType}/${action}`, { reason });
      return response.data;
    } catch (error) {
      console.error('📡 API: Manual override error:', error);
      throw error;
    }
  },

  clearManualOverride: async (agentType) => {
    try {
      const response = await api.delete(`/api/v1/agents/manual-override/${agentType}`);
      return response.data;
    } catch (error) {
      console.error('📡 API: Clear override error:', error);
      throw error;
    }
  },

  getPerformanceReport: async () => {
    try {
      const response = await api.get('/api/v1/agents/performance-report');
      return response.data;
    } catch (error) {
      console.error('📡 API: Performance report error:', error);
      throw error;
    }
  },

  getSenderScores: async () => {
    try {
      const response = await api.get('/api/v1/agents/sender-scores');
      return response.data;
    } catch (error) {
      console.error('📡 API: Sender scores error:', error);
      throw error;
    }
  },

  addEmail: async (emailData) => {
    try {
      console.log('📡 API: Adding new email:', emailData);
      const response = await api.post('/api/v1/marketing/domains/add-email', emailData);
      console.log('📡 API: Add email response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Add email error:', error);
      throw error;
    }
  },

  deleteEmail: async (email) => {
    try {
      console.log('📡 API: Deleting email:', email);
      const response = await api.delete(`/api/v1/marketing/domains/delete-email/${encodeURIComponent(email)}`);
      console.log('📡 API: Delete email response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Delete email error:', error);
      throw error;
    }
  },

  // Weekly Template Progression
  getWeeklyProgression: async () => {
    try {
      console.log('📡 API: Getting weekly progression...');
      const response = await api.get('/api/v1/weekly-templates/weeks');
      console.log('📡 API: Weekly progression response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Weekly progression error:', error);
      throw error;
    }
  },

  getWeekDetails: async (week) => {
    try {
      console.log('📡 API: Getting week details for:', week);
      const response = await api.get(`/api/v1/weekly-templates/weeks/${week}`);
      console.log('📡 API: Week details response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Week details error:', error);
      throw error;
    }
  },

  runWeeklyWarmup: async (week, emailsPerAccount = 1, category = null) => {
    try {
      console.log('📡 API: Running weekly warmup:', { week, emailsPerAccount, category });
      const response = await api.post('/api/v1/weekly-templates/run-cycle-with-week', {
        week,
        emails_per_account: emailsPerAccount,
        category
      });
      console.log('📡 API: Weekly warmup response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Weekly warmup error:', error);
      throw error;
    }
  },

  getCurrentWeek: async () => {
    try {
      console.log('📡 API: Getting current week...');
      const response = await api.get('/api/v1/weekly-templates/current-week');
      console.log('📡 API: Current week response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Current week error:', error);
      throw error;
    }
  },

  setCurrentWeek: async (week) => {
    try {
      console.log('📡 API: Setting current week to:', week);
      const response = await api.post('/api/v1/weekly-templates/set-current-week', { week });
      console.log('📡 API: Set week response:', response.data);
      return response.data;
    } catch (error) {
      console.error('📡 API: Set week error:', error);
      throw error;
    }
  },
};

export default api;
